import { createContext, useContext, useReducer, useEffect, useMemo } from 'react'

import { compareCells, rawCell } from './render'
import { isPresent, isPromise, toDollars } from '../../lib/utils'
import { omit } from 'lodash-es'
import { Column, ColumnLookupTable, DataTable, DataOrPromise, Filter, Options, Row, GroupedRow, GenericRow, LookupColumn, DataOptions } from './types'

function toNumericSortOrder(sortOrder: Options['sortOrder']) {
  if(typeof(sortOrder) === 'string') {
    if(sortOrder.toLowerCase() === 'asc') {
      return 1
    }
    if(sortOrder.toLowerCase() === 'desc') {
      return -1
    }
  }
  if(sortOrder === 1 || sortOrder === -1) {
    return sortOrder
  }
  return 1
}

function extractDefaultFilters<RowType extends GenericRow>(columns: Column<RowType>[]) {
  return Object.fromEntries(columns
    .filter(column => column.defaultFilter)
    .map(column => [column.field, column.defaultFilter])
  )
}

function isValidFilter(filter: Filter | null) {
  if(!filter || !filter.operator || !filter.value) {
    return false
  }

  switch(filter.operator) {
    case 'between':
    case 'not-between':
      return isFinite(parseFloat(filter.value[0] as string)) && isFinite(parseFloat(filter.value[1] as string))
    default:
      return isPresent(filter.value[0])
  }
}

export class DataManager<RowType extends GenericRow> {
  columns: Column<RowType>[]
  dataOrPromiseOrFn: DataOrPromise<RowType>
  options: Options
  onChangeCallback: () => void
  onChangeColumnsCallback: (columns: Column<RowType>[]) => void
  sortColumn: Column<RowType> | undefined
  sortOrder: 1 | -1
  paginated: boolean
  hasRowActions: boolean
  page: number
  pageSize: number | null
  data: DataTable<RowType>
  filters: Record<string, Filter>
  totalCount: number
  loading: boolean
  error: Error | null
  version: number

  constructor(columns: Column<RowType>[], dataOrPromiseOrFn: DataOrPromise<RowType>, options: Options, onChange: () => void, onChangeColumns: (columns: Column<RowType>[]) => void) {
    this.columns                 = columns
    this.dataOrPromiseOrFn       = dataOrPromiseOrFn
    this.options                 = options
    this.onChangeCallback        = onChange
    this.onChangeColumnsCallback = onChangeColumns
    this.sortColumn              = options.sortColumn ? columns.find(column => options.sortColumn === column.field) : undefined
    this.sortOrder               = toNumericSortOrder(options.sortOrder)
    this.paginated               = !!options.paginated
    this.hasRowActions           = !!options.hasRowActions
    this.page                    = 1
    this.pageSize                = this.paginated ? (options.pageSize || 10) : null
    this.data                    = []
    this.filters                 = extractDefaultFilters(columns)
    this.totalCount              = 0
    this.loading                 = false
    this.error                   = null
    this.version                 = 0

    this.reload()
  }

  setPageSize(pageSize: number) {
    if(this.paginated) {
      this.pageSize = pageSize
      this.page = 1
      this.reload()
    }
  }

  setPage(page: number) {
    if(this.paginated) {
      this.page = page
      this.reload()
    }
  }

  onChange() {
    if(this.onChangeCallback) {
      this.version++
      this.onChangeCallback()
    }
  }

  onChangeColumns() {
    if(this.onChangeColumnsCallback) {
      this.onChangeColumnsCallback(this.columns)
    }
    this.onChange()

    // TODO: Not sure if this is is necessary, might be worth verifying with a test if we don't have one
    // If the sort column is no longer in this.columns, try to reset it from this.options.sortColumn
    if(this.sortColumn && !this.columns.includes(this.sortColumn)) {
      this.sortColumn = this.options.sortColumn ? this.columns.find(column => this.options.sortColumn === column.field) : undefined
    }

    // If any additional promise-based lookups were added, resolve them.
    // TODO: this should set the loading state on the table, which it currently doesn't do.
    const lookupPromises = this.getLookupPromises()
    if(lookupPromises.length > 0) {
      Promise.all(lookupPromises).then(() => this.onChange())
    }
  }

  getLookupPromises() {
    return this.columns.filter(column => 'lookup' in column && isPromise(column.lookup)).map(async (column: LookupColumn<RowType, unknown>) => {
      const promise = column.lookup as Promise<ColumnLookupTable<unknown>>
      const lookup = await promise
      // TODO: can we avoid modifying columns here (since columns is a passed-in value)?
      column.lookup = lookup
    })
  }

  reload(forceReload = false) {
    if(this.loading && !forceReload) {
      return
    }

    this.loading = true
    this.error   = null

    this.onChange()

    const dataOrPromiseOrFn = this.dataOrPromiseOrFn
    const filters = this.filters
    const isStale = () => (dataOrPromiseOrFn !== this.dataOrPromiseOrFn) || (filters !== this.filters)
    const dataFn = typeof(this.dataOrPromiseOrFn) === 'function' ? this.dataOrPromiseOrFn : (_: unknown) => this.dataOrPromiseOrFn
    const dataPromise = Promise.resolve(dataFn(this.getDataOptions()))
    const dataAndLookupsPromise = Promise.all([dataPromise, ...this.getLookupPromises()])

    dataAndLookupsPromise
      .then(([data, ..._lookups]) => {
        if(isStale()) {
          return
        }
        if(typeof(data) === 'object' && 'data' in data && data.data instanceof Array) {
          this.data = data.data
          this.totalCount = data.totalCount ? data.totalCount : this.data.length
        } else if(typeof(data) === 'object' && data instanceof Array) {
          this.data = data
          this.totalCount = data.length
        } else {
          this.data = []
          this.totalCount = 0
        }
        if(this.sortColumn !== undefined) {
          const sortColumn = this.sortColumn
          this.data = [...this.data].sort((row1, row2) => this.sortOrder * compareCells(rawCell(row1, sortColumn), rawCell(row2, sortColumn)))
        }
      })
      .catch(error => {
        if(isStale()) {
          return
        }
        this.data  = []
        this.totalCount = 0
        this.error = error
      })
      .finally(() => {
        if(isStale()) {
          this.reload(true)
          return
        }
        this.loading = false
        this.onChange()
      })
  }

  getVisibleColumns() {
    return this.columns.filter(column => column.visible)
  }

  // Once the special casing is removed, this can be made more general (for future instances of SimpleTable export)
  getCsvAllocationData() {
    const csv: string[][] = []
    // It's unclear how to handle smart score, so it's filtered out for the time being
    const filteredColumns: Column<RowType>[] = this.getVisibleColumns().filter(column => column.field !== 'smartScore')
    const headers = filteredColumns.map(column => column.title)
    csv.push(headers)
    this.data.forEach(row => {
      csv.push(filteredColumns.map(column => {
        let cellValue: string
        const rawValue = rawCell(row, column)
        if(typeof(rawValue) === 'string' || typeof(rawValue) === 'number') {
          cellValue = String(rawValue)
        }
        else {
          cellValue = ''
        }

        // This special-casing isn't ideal, but there's no way to infer that a column is a "currency" type
        if(('cplInCents' === column.field || 'budgetInCents' === column.field || 'totalCostInCents' === column.field) && cellValue !== '') {
          cellValue = toDollars(cellValue)
        }

        return cellValue
      }))
    })

    return csv.map(row => row.map(column => `"${String(column).replace('"', '"""')}"`).join(',')).join("\n")
  }

  getColSpan() {
    return this.getVisibleColumns().length + (this.hasRowActions ? 1 : 0)
  }

  setColumns(columns: Column<RowType>[]) {
    const oldColumns = this.columns
    this.columns = columns
    if(oldColumns !== columns) {
      this.onChangeColumns()
    }
  }

  setData(dataOrPromiseOrFn: DataOrPromise<RowType>) {
    if(dataOrPromiseOrFn !== this.dataOrPromiseOrFn) {
      this.dataOrPromiseOrFn = dataOrPromiseOrFn
      this.reload()
    }
  }

  setFilter(field: string, filter: Filter | null) {
    if(filter && isValidFilter(filter)) {
      this.filters = {...this.filters, [field]: filter}
    } else {
      this.filters = omit(this.filters, [field])
    }

    if(this.paginated) {
      this.page = 1
      this.reload()
    } else {
      this.onChange()
    }
  }

  clearFilter(column: Column<RowType>) {
    if (!column.field) return
    this.setFilter(column.field, null)
  }

  hasFilter(column: Column<RowType>): boolean {
    return !!column.field && isValidFilter(this.filters[column.field])
  }

  resetFilters() {
    this.filters = extractDefaultFilters(this.columns)

    if(this.paginated) {
      this.page = 1
      this.reload()
    } else {
      this.onChange()
    }
  }

  sortBy(column: Column<RowType>) {
    if(this.loading || this.error) {
      return
    }

    if(this.sortColumn !== column) {
      this.sortColumn = column
      this.sortOrder = 1
    }
    else {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1
    }

    if(this.paginated) {
      this.reload()
    } else {
      this.data = [...this.data].sort((row1, row2) => this.sortOrder * compareCells(rawCell(row1, column), rawCell(row2, column)))
      this.onChange()
    }
  }

  getDataOptions(): DataOptions | undefined {
    if(this.paginated) {
      return {
        sortColumn: this.sortColumn && this.sortColumn.field || null,
        sortOrder:  this.sortColumn && this.sortColumn.field ? (this.sortOrder === 1 ? 'asc' : this.sortOrder === -1 ? 'desc' : null) : null,
        page:       this.page,
        pageSize:   this.pageSize,
        filters:    Object.entries(this.filters).filter(([_field, filter]) => isPresent(isValidFilter(filter))).map(([field, filter]) => ({field, ...filter}))
      }
    }
  }

  group(ungroupedData: DataTable<RowType>): GroupedRow<RowType>[] {
    // groupingColumns is the groupable prefix of visible columns
    const groupingColumns = this.getVisibleColumns().reduce((list, column, idx) => list.length === idx && column.groupable ? [...list, column] : list, [])

    const groups = groupingColumns.length === 0 ? [] : {}
    ungroupedData.forEach(row => {
      const group = groupingColumns.reduce((group, col, j) => {
        const val = "x" + rawCell(row, col) // prefix the key with a letter, because Object.keys() does not respect insertion order when the keys are numeric
        return group[val] ? group[val] : group[val] = (j === groupingColumns.length - 1 ? [] : {})
      }, groups)
      group.push(row)
    })

    return this.populateFromGroups(groups)
  }

  populateFromGroups(groups: Row<RowType>[] | Record<string,Row<RowType>[]>): GroupedRow<RowType>[] {
    if(groups instanceof Array) {
      return groups.map((row) => ({row, rowSpans: [], skipColumns: []}))
    } else {
      const data: GroupedRow<RowType>[] = []
      Object.values(groups).forEach(subgroups => {
        const subresult = this.populateFromGroups(subgroups)
        data.push(...subresult.map(({row, rowSpans, skipColumns}, index) => ({row, rowSpans: [subresult.length, ...rowSpans], skipColumns: [index > 0, ...skipColumns]})))
      })
      return data
    }
  }
}

type StateType = Record<string,never>
export type DataManagerContext<RowType extends GenericRow> = { dataManager: DataManager<RowType>, state: StateType }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataManagerContext = createContext<DataManagerContext<any> | undefined>(undefined)

export function DataManagerProvider({columns, data, options, children, onChange, onChangeColumns, dataManagerRef}) {
  const [state, dispatch] = useReducer(_state => ({}), {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataManager = useMemo(() => new DataManager(columns, data, options, dispatch, onChangeColumns), [])

  useEffect(() => {
    onChange?.()
  }, [onChange, state])

  if(dataManagerRef) {
    dataManagerRef.current = dataManager
  }
  dataManager.setColumns(columns)
  dataManager.setData(data)

  return (
    <DataManagerContext.Provider value={{dataManager, state}}>
      {children}
    </DataManagerContext.Provider>
  )
}

export function useDataManager<RowType extends GenericRow>(): DataManager<RowType> {
  const context = useContext<DataManagerContext<RowType> | undefined>(DataManagerContext)
  if(context === undefined) {
    throw new Error("Can't call useDataManager without a DataManagerContext")
  }
  return context.dataManager
}
